import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { compose } from "redux";
import {
  ActionsFooter,
  ActionsFooterRenderControl,
  Button,
  printCrossBrowser,
  Head,
} from "component-library";

import { IBreakdown } from "shared/models/breakdown";
import { FacetCategory } from "shared/models/facet";
import { IUser } from "shared/models/user";

import { PageTitleKey } from "config/common";
import PageKey from "config/PageKey";

import { IApplicationState } from "store";
import { getFacets } from "store/facet/facet.actions";
import { FacetStoreKey, GetFacets } from "store/facet/facet.types";
import { getCountries } from "store/location/location.actions";
import { GetCountries } from "store/location/location.types";
import { getBreakdownPage } from "store/page/page.actions";
import { GetBreakdownPage } from "store/page/page.types";
import { getPageTranslation } from "store/translation/translation.actions";
import {
  GetPageTranslation,
  ITranslationState,
} from "store/translation/translation.types";
import { hidePopup, setLoading, showPopup } from "store/ui/ui.actions";
import { HidePopup, ShowPopup } from "store/ui/ui.types";

import { IPrepareBreakdownViewData } from "helpers/breakdownViewHelpers";
import { getPageTitle } from "helpers/getPageTitle";

import withPageData, {
  ILoaders,
  IWithPageDataProps,
  mapStateToWithPageDataProps,
} from "hoc/withPageData";

import BreakdownView, {
  IBreakdownViewRole,
  IBreakdownViewTexts,
} from "components/BreakdownView/BreakdownView";

import {
  prepareBreakdownViewRoles,
  prepareBreakdownViewTexts,
  prepareTexts,
} from "./BreakdownPrintPage.helpers";
import { WithTranslation, withTranslation } from "i18n";
import {
  FEATURE_FLAGS,
  LaunchDarklyClientWithFlags,
  withLaunchDarklyConsumerHOC,
} from "shared-services";

export interface IBreakdownPrintPageTexts {
  printSubmitButton: string;
  printCancelButton: string;
}

export interface IBreakdownPrintPageActions {
  getPageTranslation: GetPageTranslation;
  getBreakdownPage: GetBreakdownPage;
  getCountries: GetCountries;
  getFacets: GetFacets;
  showPopup: ShowPopup;
  hidePopup: HidePopup;
}

export interface IBreakdownPrintPageMatchParams {
  breakdownId: string;
}

export interface IBreakdownPrintPageStoreProps {
  texts: IBreakdownPrintPageTexts;
  translation: ITranslationState;
  breakdown: IBreakdown;
  breakdownViewTexts: IBreakdownViewTexts;
  breakdownRoles: IBreakdownViewRole[];
  pageTitle: string;
  user: IUser;
}

export interface IBreakdownPrintPageComposedProps
  extends IBreakdownPrintPageActions,
    IBreakdownPrintPageStoreProps,
    IWithPageDataProps,
    RouteComponentProps<IBreakdownPrintPageMatchParams> {}

export interface IBreakdownPrintPageProps
  extends IBreakdownPrintPageComposedProps,
    WithTranslation,
    LaunchDarklyClientWithFlags {}

export class BreakdownPrintPage extends React.Component<IBreakdownPrintPageProps> {
  public render() {
    const { pageTitle, breakdown, breakdownRoles, breakdownViewTexts, user } =
      this.props;

    return (
      <>
        <section className="c-breakdown-print-page">
          <Head registerPageView={this.registerPageView}>
            <title>{pageTitle}</title>
          </Head>
          <BreakdownView
            breakdown={breakdown}
            roles={breakdownRoles}
            texts={breakdownViewTexts}
            limitWidth={true}
            user={user}
            isPrintView={true}
          />
        </section>
        <ActionsFooter
          isSticky={true}
          variant="secondary"
          fullWidth={true}
          renderControl={this.renderActionsFooterControl}
        />
      </>
    );
  }

  private renderActionsFooterControl: ActionsFooterRenderControl = () => {
    const {
      texts: { printCancelButton, printSubmitButton },
    } = this.props;

    return (
      <>
        <div className="g-col-lg-3 g-col-md-4 g-col-sm-12 g-start-lg-7 g-start-md-5 g-start-sm-1 g-row-start-sm-3">
          <Button
            text={printCancelButton}
            type="secondary"
            onClick={this.handleCloseClick}
          />
        </div>
        <div className="g-col-lg-3 g-col-md-4 g-col-sm-12 g-start-lg-10 g-start-md-9 g-start-sm-1 g-row-start-sm-2">
          <Button
            text={printSubmitButton}
            type="primary"
            onClick={this.handlePrintClick}
          />
        </div>
      </>
    );
  };

  private handleCloseClick = () => {
    window.close();
  };

  private handlePrintClick = () => {
    printCrossBrowser();
  };

  private registerPageView = () => {
    // .keep
  };
}

async function loadExternalData(props: IBreakdownPrintPageProps) {
  return Promise.all([
    props.getCountries(props.locale),
    props.getFacets(
      [
        FacetCategory.ContractType,
        FacetCategory.ProductionType,
        FacetCategory.Gender,
        FacetCategory.Appearance,
        FacetCategory.Disability,
        FacetCategory.ComplaintCategory,
      ],
      props.locale
    ),
  ]);
}

const pageDataLoaders: ILoaders<IBreakdownPrintPageProps> = {
  loadTranslation(props, isLocaleChanged) {
    return props.getPageTranslation(
      { pageKey: PageKey.Breakdown, locale: props.locale },
      { throwOnError: isLocaleChanged }
    );
  },
  async loadData(props) {
    await loadExternalData(props);

    return props.getBreakdownPage(
      props.match.params.breakdownId.toLowerCase(),
      props.cancellationToken
    );
  },
  loadOnLocaleChange: loadExternalData,
};

const mapStateToProps = (
  state: IApplicationState,
  props: IBreakdownPrintPageProps
): IBreakdownPrintPageStoreProps => {
  const { t, flags } = props;
  const isYPPlayingAgeEnabled = flags[FEATURE_FLAGS.YPsPlayingAgeMin];
  const pageTranslation = state.translation.pages[PageKey.Breakdown]!;
  const commonTranslation = state.translation.common;
  const breakdown = state.page.data!.breakdown!;
  const prepareBreakdownViewData: IPrepareBreakdownViewData = {
    productionTypes: state.facet[FacetStoreKey.ProductionType]!,
    genders: state.facet[FacetStoreKey.Gender]!,
    disabilities: state.facet[FacetStoreKey.Disability]!,
    appearances: state.facet[FacetStoreKey.Appearance]!,
    contractTypes: state.facet[FacetStoreKey.ContractType]!,
    countries: state.location.countries!,
  };
  const breakdownViewTexts = prepareBreakdownViewTexts(
    pageTranslation,
    state.translation.common,
    breakdown,
    prepareBreakdownViewData
  );

  return {
    texts: prepareTexts(state.translation.common),
    translation: state.translation,
    breakdown,
    breakdownViewTexts,
    breakdownRoles: prepareBreakdownViewRoles({
      pageTranslation,
      breakdown,
      prepareBreakdownViewData,
      t,
      isYPPlayingAgeEnabled,
    }),
    pageTitle: getPageTitle(
      commonTranslation,
      PageTitleKey.Breakdown,
      (format) => format.replace("{breakdownName}", breakdownViewTexts.title)
    ),
    user: state.user.data!,
  };
};

const mapDispatchToProps = {
  setLoading,
  getPageTranslation,
  getBreakdownPage,
  getCountries,
  getFacets,
  showPopup,
  hidePopup,
};

export default compose(
  withTranslation(),
  withLaunchDarklyConsumerHOC(),
  connect(mapStateToWithPageDataProps, mapDispatchToProps),
  withPageData<IBreakdownPrintPageProps>(PageKey.Breakdown, pageDataLoaders),
  connect(mapStateToProps, mapDispatchToProps)
)(BreakdownPrintPage) as React.ComponentType<IBreakdownPrintPageComposedProps>;
